











































































































import { Component, Mixins } from 'vue-property-decorator';
import TermsDialog from '@/components/TermsDialog.vue';
import { EmailVerificationResend } from '@/api';
import { authModule } from '@/store';
import ResendCooldown from '@/mixins/ResendCooldown';
import AcceptTerms from '@/mixins/AcceptTerms';
import {
  deadlinePhrase,
  gracePeriod,
  noticeSeverity,
  outstandingObligations,
  NoticeSeverity,
  Obligations,
} from '@/accountNotices';

/**
 * The page a user is sent to, or linked to, when their account has something
 * outstanding: confirming their email address, agreeing to the terms, or both.
 *
 * This is the "verification page" of the brief's "redirects unverified users to
 * verification page on next login". It is deliberately *not* `/verify-email`,
 * which is a token consumer: that route reads a token out of the emailed link
 * and, with no token, renders "your address is probably already confirmed" -
 * the exact opposite of what a user sent here needs to hear.
 *
 * With nothing outstanding it reads as an all-clear rather than an error. A
 * user who verifies and accepts in one visit ends up looking at exactly that,
 * and so does anyone who bookmarks the page.
 *
 * The deadline comes from the user resource's `gracePeriod` field (P4-09), read
 * through `@/accountNotices`. With `GRACE_PERIOD_END` unset - the shipping state
 * - there is no deadline and this page says nothing about timing, exactly as it
 * did before. Within a week of the last day it names the days remaining, and
 * once the window has closed it says plainly that bidding is blocked until both
 * obligations are met.
 */
@Component({
  components: { TermsDialog },
})
export default class AccountNotices extends Mixins(
  ResendCooldown,
  AcceptTerms,
) {
  showTerms: boolean = false;

  resending: boolean = false;

  message: string = '';

  messageType: string = 'info';

  /**
   * Tracked locally as well as in the store, the way `TermsAlert` used to.
   *
   * Accepting mutates `termsAcceptanceRequired` on the stored spraypaint
   * instance, and whether Vue observes an attribute behind spraypaint's
   * accessors is an implementation detail of both libraries. On this page the
   * card vanishing is the only feedback the user gets, so it does not ride on
   * that bet.
   */
  acceptedHere: boolean = false;

  get obligations(): Obligations {
    const obligations = outstandingObligations();
    if (!this.acceptedHere) {
      return obligations;
    }
    return {
      ...obligations,
      terms: false,
      any: obligations.verification,
    };
  }

  get email(): string {
    const user = authModule.user;
    return user ? user.email : '';
  }

  /**
   * True once the window has shut and this user is actually being refused.
   *
   * The one place in the SPA that states the consequence *before* a refusal.
   * `VerificationGateDialog` says it too, but only after a bid has already
   * bounced, which is what this exists to get ahead of. The banner only points
   * here; spelling out the consequence is this page's job.
   */
  get gateClosed(): boolean {
    return gracePeriod().kind === 'closed';
  }

  /** The deadline as a sentence, once it is close enough to name. '' otherwise. */
  get deadline(): string {
    return deadlinePhrase();
  }

  /**
   * The colour for this page's two deadline alerts, shared with the banner.
   *
   * Both use it, so the closed-window notice and the countdown cannot end up
   * different colours on the same screen. When `gateClosed` is true this is
   * always `error`; it is read from the same helper regardless rather than
   * hardcoded, so there is one answer to "how urgent is this account".
   */
  get severity(): NoticeSeverity {
    return noticeSeverity();
  }

  get intro(): string {
    return this.obligations.verification && this.obligations.terms
      ? 'There are two things left to do before you can bid.'
      : 'There is one thing left to do before you can bid.';
  }

  /**
   * Where the user was headed before login sent them here, if anywhere.
   *
   * Carried as `continue` rather than `next` on purpose: `App.vue` watches
   * `?next=` and would redirect straight back out of this page, which is the
   * one thing that must not happen to it.
   */
  get continueTo(): string {
    const to = this.$route.query.continue;
    const path = Array.isArray(to) ? to[0] || '' : to || '';
    // Same-site paths only. An absolute URL from the query string is an open
    // redirect, and this value reaches here from `?next=` on the login page,
    // which anybody can put anything in.
    return path.startsWith('/') && !path.startsWith('//') ? path : '';
  }

  async resend() {
    if (this.cooldownActive) {
      return;
    }
    this.resending = true;
    this.message = '';
    try {
      await EmailVerificationResend.request(this.email);
      // The endpoint reports generic success whether or not the address exists
      // or is already verified, so this must not claim more than that.
      this.messageType = 'info';
      this.message =
        'If that address needs confirming, another email is on its way.';
    } catch (e) {
      const status = e.response ? e.response.status : null;
      if (status === 429) {
        this.messageType = 'warning';
        this.message = 'Please wait a moment before requesting another email.';
      } else {
        this.messageType = 'error';
        this.message = 'That did not work just now. Please try again shortly.';
      }
    } finally {
      this.resending = false;
      // Every outcome starts the countdown, including the failures - a request
      // that errored is exactly the one a user retries in a hurry.
      this.startCooldown();
    }
  }

  async accept() {
    if (await this.acceptTerms()) {
      this.acceptedHere = true;
    }
    // Closed either way, so the error is visible on the card behind it. The
    // card itself disappears on success, because `termsAcceptanceRequired` is
    // now false.
    this.showTerms = false;
  }
}
